import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, CheckCircle, ArrowRight, Zap, Shield, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';

// All Vancouver Island locations with details
const locations = {
  // Greater Victoria
  'victoria': {
    name: 'Victoria',
    region: 'Greater Victoria',
    description: 'As the capital city of British Columbia, Victoria is our home base. We provide fast, reliable electrical services throughout downtown Victoria, James Bay, Fairfield, Fernwood, and all surrounding neighborhoods.',
    highlights: ['Same-day service available', 'Downtown & residential specialists', 'Heritage home wiring experts'],
    population: '95,000+',
    driveTime: '0-20 min'
  },
  'saanich': {
    name: 'Saanich',
    region: 'Greater Victoria',
    description: 'From Gordon Head to Royal Oak, Broadmead to Cordova Bay, we serve all of Saanich with professional electrical services. Our team knows the unique needs of Saanich homes and businesses.',
    highlights: ['Serving all Saanich neighborhoods', 'Residential & commercial', 'Fast response times'],
    population: '120,000+',
    driveTime: '10-25 min'
  },
  'oak-bay': {
    name: 'Oak Bay',
    region: 'Greater Victoria',
    description: 'Oak Bay\'s beautiful heritage homes require electricians who understand older wiring systems. We specialize in upgrading and maintaining electrical systems in character homes while preserving their charm.',
    highlights: ['Heritage home specialists', 'Panel upgrades for older homes', 'Careful, respectful service'],
    population: '18,000+',
    driveTime: '10-20 min'
  },
  'esquimalt': {
    name: 'Esquimalt',
    region: 'Greater Victoria',
    description: 'We proudly serve the Esquimalt community including the naval base area. From residential homes to commercial properties, our licensed electricians deliver quality workmanship.',
    highlights: ['Residential & commercial', 'Quick response times', 'Military family friendly'],
    population: '17,000+',
    driveTime: '10-15 min'
  },
  'langford': {
    name: 'Langford',
    region: 'West Shore',
    description: 'Langford is one of the fastest-growing cities in BC, and we\'re growing with it. From new construction to existing homes in Bear Mountain, Happy Valley, and Goldstream, we\'ve got you covered.',
    highlights: ['New construction specialists', 'Rapid growth area experts', 'EV charger installations'],
    population: '46,000+',
    driveTime: '15-25 min'
  },
  'colwood': {
    name: 'Colwood',
    region: 'West Shore',
    description: 'Serving Colwood from Royal Bay to Royal Roads, our electricians provide comprehensive electrical services for this growing West Shore community.',
    highlights: ['New home wiring', 'Panel upgrades', 'Outdoor lighting'],
    population: '18,000+',
    driveTime: '15-25 min'
  },
  'view-royal': {
    name: 'View Royal',
    region: 'West Shore',
    description: 'View Royal residents trust Island Coastal Electric for all their electrical needs. We service homes along the Portage Inlet and throughout the community.',
    highlights: ['Residential focus', 'Reliable service', 'Local electricians'],
    population: '11,000+',
    driveTime: '10-20 min'
  },
  'sooke': {
    name: 'Sooke',
    region: 'West Shore',
    description: 'We regularly serve Sooke and the surrounding areas including Otter Point, East Sooke, and Shirley. Rural properties welcome - we\'re equipped for off-grid and challenging installations.',
    highlights: ['Rural property specialists', 'Generator installations', 'Off-grid solutions'],
    population: '15,000+',
    driveTime: '30-45 min'
  },
  'metchosin': {
    name: 'Metchosin',
    region: 'West Shore',
    description: 'Metchosin\'s rural properties have unique electrical needs. From hobby farms to acreages, we provide reliable electrical services throughout this beautiful community.',
    highlights: ['Acreage & farm electrical', 'Generator backup systems', 'Rural specialists'],
    population: '5,000+',
    driveTime: '25-35 min'
  },
  'highlands': {
    name: 'Highlands',
    region: 'West Shore',
    description: 'The Highlands community trusts us for electrical work on rural properties. We understand the challenges of servicing homes in this scenic area.',
    highlights: ['Rural property experience', 'Backup power solutions', 'Reliable service'],
    population: '2,500+',
    driveTime: '20-30 min'
  },
  // Saanich Peninsula
  'sidney': {
    name: 'Sidney',
    region: 'Saanich Peninsula',
    description: 'Sidney-by-the-Sea deserves quality electrical service. We serve Sidney\'s residential and commercial properties, including the busy downtown core and surrounding areas.',
    highlights: ['Marine & residential', 'Commercial services', 'Quick peninsula response'],
    population: '12,000+',
    driveTime: '25-35 min'
  },
  'north-saanich': {
    name: 'North Saanich',
    region: 'Saanich Peninsula',
    description: 'From the airport area to Deep Cove, North Saanich properties receive our full range of electrical services. Agricultural and residential specialists.',
    highlights: ['Agricultural electrical', 'Large property specialists', 'Airport area service'],
    population: '12,000+',
    driveTime: '25-40 min'
  },
  'central-saanich': {
    name: 'Central Saanich',
    region: 'Saanich Peninsula',
    description: 'Brentwood Bay, Saanichton, and all of Central Saanich trust Island Coastal Electric. We serve farms, wineries, and residential properties throughout.',
    highlights: ['Winery & farm electrical', 'Residential services', 'Commercial projects'],
    population: '17,000+',
    driveTime: '20-30 min'
  },
  // Cowichan Valley
  'duncan': {
    name: 'Duncan',
    region: 'Cowichan Valley',
    description: 'Duncan and the Cowichan Valley have trusted electrical contractors in Island Coastal Electric. We provide full electrical services to this vibrant community.',
    highlights: ['Full Cowichan coverage', 'Residential & commercial', 'Agricultural electrical'],
    population: '5,000+',
    driveTime: '45-60 min'
  },
  'north-cowichan': {
    name: 'North Cowichan',
    region: 'Cowichan Valley',
    description: 'From Maple Bay to Crofton, Chemainus to Saltair, North Cowichan receives comprehensive electrical services from our experienced team.',
    highlights: ['Large service area', 'Industrial experience', 'Marine electrical'],
    population: '32,000+',
    driveTime: '45-75 min'
  },
  'chemainus': {
    name: 'Chemainus',
    region: 'Cowichan Valley',
    description: 'The famous mural town of Chemainus deserves quality electrical work. We serve residential and commercial properties throughout this charming community.',
    highlights: ['Heritage building experience', 'Residential services', 'Commercial projects'],
    population: '4,000+',
    driveTime: '50-60 min'
  },
  'lake-cowichan': {
    name: 'Lake Cowichan',
    region: 'Cowichan Valley',
    description: 'Lake Cowichan and the surrounding lake communities trust us for reliable electrical services. Cabin, cottage, and year-round home specialists.',
    highlights: ['Lakefront properties', 'Cabin electrical', 'Seasonal service'],
    population: '3,500+',
    driveTime: '60-75 min'
  },
  'ladysmith': {
    name: 'Ladysmith',
    region: 'Cowichan Valley',
    description: 'Ladysmith\'s historic downtown and growing residential areas receive expert electrical service from Island Coastal Electric.',
    highlights: ['Heritage home wiring', 'New construction', 'Commercial services'],
    population: '9,000+',
    driveTime: '55-65 min'
  },
  'cobble-hill': {
    name: 'Cobble Hill',
    region: 'Cowichan Valley',
    description: 'Cobble Hill\'s farms, wineries, and rural properties have unique electrical needs. We\'re experienced with agricultural and residential electrical systems.',
    highlights: ['Winery electrical', 'Farm services', 'Rural specialists'],
    population: '5,000+',
    driveTime: '40-50 min'
  },
  'mill-bay': {
    name: 'Mill Bay',
    region: 'Cowichan Valley',
    description: 'Mill Bay residents enjoy reliable electrical service from our team. We serve everything from waterfront properties to rural acreages.',
    highlights: ['Waterfront specialists', 'Residential focus', 'Reliable service'],
    population: '3,500+',
    driveTime: '35-45 min'
  },
  'shawnigan-lake': {
    name: 'Shawnigan Lake',
    region: 'Cowichan Valley',
    description: 'Shawnigan Lake cabins and homes receive expert electrical service. We understand the unique needs of lakefront and seasonal properties.',
    highlights: ['Lakefront electrical', 'Cabin upgrades', 'Year-round service'],
    population: '8,000+',
    driveTime: '35-50 min'
  },
  // Central Island
  'nanaimo': {
    name: 'Nanaimo',
    region: 'Central Vancouver Island',
    description: 'As the Hub City of Vancouver Island, Nanaimo receives full electrical services from Island Coastal Electric. From downtown to north Nanaimo, we\'ve got you covered.',
    highlights: ['Full city coverage', 'Commercial & residential', 'Industrial experience'],
    population: '99,000+',
    driveTime: '90-110 min'
  },
  'lantzville': {
    name: 'Lantzville',
    region: 'Central Vancouver Island',
    description: 'Lantzville\'s oceanfront and rural properties deserve quality electrical service. We serve this growing community north of Nanaimo.',
    highlights: ['Oceanfront specialists', 'Rural properties', 'Quality service'],
    population: '4,000+',
    driveTime: '95-105 min'
  },
  'parksville': {
    name: 'Parksville',
    region: 'Central Vancouver Island',
    description: 'Parksville\'s resort community and residential areas receive professional electrical services. From beachfront to inland properties.',
    highlights: ['Resort area experience', 'Retirement community', 'Residential focus'],
    population: '13,000+',
    driveTime: '105-120 min'
  },
  'qualicum-beach': {
    name: 'Qualicum Beach',
    region: 'Central Vancouver Island',
    description: 'Beautiful Qualicum Beach deserves beautiful electrical work. We serve this desirable community with full electrical services.',
    highlights: ['Retirement community', 'Residential specialists', 'Quality workmanship'],
    population: '9,000+',
    driveTime: '110-125 min'
  },
  'port-alberni': {
    name: 'Port Alberni',
    region: 'Central Vancouver Island',
    description: 'Port Alberni and the Alberni Valley trust Island Coastal Electric for residential and commercial electrical services.',
    highlights: ['Valley-wide service', 'Industrial experience', 'Residential & commercial'],
    population: '18,000+',
    driveTime: '120-150 min'
  },
  'tofino': {
    name: 'Tofino',
    region: 'West Coast',
    description: 'Tofino\'s unique coastal environment requires electricians who understand marine conditions. We serve this world-famous surf town.',
    highlights: ['Coastal specialists', 'Resort & hospitality', 'Corrosion-resistant solutions'],
    population: '2,500+',
    driveTime: '180-210 min'
  },
  'ucluelet': {
    name: 'Ucluelet',
    region: 'West Coast',
    description: 'Ucluelet\'s fishing community and growing tourism industry receive reliable electrical services from our experienced team.',
    highlights: ['Marine environment', 'Commercial fishing', 'Tourism industry'],
    population: '2,000+',
    driveTime: '175-200 min'
  },
  // Comox Valley
  'courtenay': {
    name: 'Courtenay',
    region: 'Comox Valley',
    description: 'Courtenay is the heart of the Comox Valley, and we\'re proud to serve this growing community with professional electrical services.',
    highlights: ['Comox Valley hub', 'Residential & commercial', 'Growing community'],
    population: '28,000+',
    driveTime: '140-160 min'
  },
  'comox': {
    name: 'Comox',
    region: 'Comox Valley',
    description: 'The town of Comox and surrounding areas receive expert electrical service. From the marina to residential neighborhoods.',
    highlights: ['Marina area service', 'Residential focus', 'Military base area'],
    population: '15,000+',
    driveTime: '145-165 min'
  },
  'cumberland': {
    name: 'Cumberland',
    region: 'Comox Valley',
    description: 'Cumberland\'s historic village and growing population trust us for quality electrical work that respects the community\'s character.',
    highlights: ['Heritage buildings', 'Village specialists', 'Growing community'],
    population: '4,500+',
    driveTime: '150-170 min'
  },
  // North Island
  'campbell-river': {
    name: 'Campbell River',
    region: 'North Vancouver Island',
    description: 'Campbell River, the Salmon Capital of the World, receives full electrical services from Island Coastal Electric. Industrial, commercial, and residential.',
    highlights: ['Industrial experience', 'Full service coverage', 'North Island hub'],
    population: '35,000+',
    driveTime: '170-190 min'
  },
  'port-hardy': {
    name: 'Port Hardy',
    region: 'North Vancouver Island',
    description: 'Even the north tip of Vancouver Island deserves quality electrical service. We travel to Port Hardy for larger projects.',
    highlights: ['North Island service', 'Industrial projects', 'Commercial work'],
    population: '4,000+',
    driveTime: '300+ min'
  },
  'port-mcneill': {
    name: 'Port McNeill',
    region: 'North Vancouver Island',
    description: 'Port McNeill and the north island communities receive electrical services for residential and commercial projects.',
    highlights: ['North Island coverage', 'Forestry industry', 'Commercial services'],
    population: '2,500+',
    driveTime: '270-290 min'
  },
  'gold-river': {
    name: 'Gold River',
    region: 'North Vancouver Island',
    description: 'Gold River\'s remote location doesn\'t mean compromising on electrical quality. We serve this community for significant projects.',
    highlights: ['Remote community service', 'Industrial experience', 'Quality workmanship'],
    population: '1,200+',
    driveTime: '200-230 min'
  },
  'sayward': {
    name: 'Sayward',
    region: 'North Vancouver Island',
    description: 'Sayward and the Sayward Valley receive electrical services from our team. Forestry, residential, and commercial.',
    highlights: ['Valley coverage', 'Forestry industry', 'Residential service'],
    population: '500+',
    driveTime: '200-220 min'
  },
  // General Vancouver Island
  'vancouver-island': {
    name: 'Vancouver Island',
    region: 'Island-Wide',
    description: 'Island Coastal Electric proudly serves ALL of Vancouver Island. From Victoria to Port Hardy, Tofino to Campbell River, we bring professional electrical services to every community on the island. Our mobile team travels the entire island for residential, commercial, and industrial projects.',
    highlights: ['Island-wide coverage', 'All communities served', 'Mobile service team', 'No job too remote'],
    population: '870,000+',
    driveTime: 'Varies by location'
  }
};

const services = [
  { name: 'EV Charger Installation', slug: 'ev-charger-installation', icon: '⚡' },
  { name: 'Panel Upgrades', slug: 'panel-replacement', icon: '🔌' },
  { name: 'Generator Installation', slug: 'generator-installation', icon: '🔋' },
  { name: 'Residential Electrical', slug: 'residential-electrical', icon: '🏠' },
  { name: 'Commercial Electrical', slug: 'commercial-electrical', icon: '🏢' },
  { name: 'Lighting Installation', slug: 'lighting-installation', icon: '💡' }
];

const LocationPage = () => {
  const { slug } = useParams();
  const location = locations[slug];

  const title = location ? ('Electrician in ' + location.name + ' | Island Coastal Electric') : 'Location Not Found';
  const description = location ? ('Professional electrical services in ' + location.name + ', BC. Licensed electricians for EV chargers, panel upgrades, generators & more. Call 778-960-2377 for free estimates!') : '';
  const canonicalUrl = 'https://islandcoastalelectric.ca/areas/' + slug;
  const keywords = location ? ('electrician ' + location.name + ', electrical contractor ' + location.name + ', EV charger installation ' + location.name + ', panel upgrade ' + location.name + ', ' + location.name + ' BC electrician, licensed electrician ' + location.name) : '';

  useEffect(() => {
    document.title = title;
    if (!location) return;
    const setMeta = (name, content, property) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector('meta[' + attr + '="' + name + '"]');
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('geo.placename', location.name);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', canonicalUrl);
  }, [title, description, canonicalUrl, keywords, location]);

  if (!location) {
    return (
      <div className="min-h-screen bg-primary-dark pt-32 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Location Not Found</h1>
          <Link to="/areas" className="text-electric-teal hover:underline">View all service areas</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Electrician",
        "name": `Island Coastal Electric - ${location.name}`,
        "url": `https://islandcoastalelectric.ca/areas/${slug}`,
        "telephone": "+17789602377",
        "email": "info@islandcoastalelectric.ca",
        "description": description,
        "address": { "@type": "PostalAddress", "addressLocality": location.name, "addressRegion": "BC", "addressCountry": "CA" },
        "areaServed": { "@type": "City", "name": location.name, "containedInPlace": { "@type": "AdministrativeArea", "name": "Vancouver Island, British Columbia" } },
        "priceRange": "$$",
        "openingHoursSpecification": [
          { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "18:00" },
          { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "08:00", "closes": "16:00" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `Electrical Services in ${location.name}`,
          "itemListElement": services.map(s => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": s.name, "areaServed": location.name } }))
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://islandcoastalelectric.ca" },
          { "@type": "ListItem", "position": 2, "name": "Areas We Serve", "item": "https://islandcoastalelectric.ca/areas" },
          { "@type": "ListItem", "position": 3, "name": location.name, "item": `https://islandcoastalelectric.ca/areas/${slug}` }
        ]
      }) }} />

      <div className="min-h-screen bg-primary-dark pt-28 lg:pt-32">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-secondary-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 text-electric-teal mb-4">
                <MapPin className="w-5 h-5" />
                <span className="font-bold uppercase tracking-wider text-sm">{location.region}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                Electrician in {location.name}
              </h1>
              <p className="text-white/70 text-lg md:text-xl max-w-3xl mb-8">
                {location.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-electric-teal text-primary-dark font-bold hover:bg-electric-teal/90">
                  <Link to="/book-estimate">
                    Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <a href="tel:778-960-2377">
                    <Phone className="mr-2 w-5 h-5" /> 778-960-2377
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Quick Facts */}
        <section className="py-12 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-electric-teal text-3xl font-bold">{location.population}</p>
                <p className="text-white/60 text-sm">Population Served</p>
              </div>
              <div className="text-center">
                <p className="text-electric-teal text-3xl font-bold">{location.driveTime}</p>
                <p className="text-white/60 text-sm">From Our Shop</p>
              </div>
              <div className="text-center">
                <p className="text-electric-teal text-3xl font-bold">20+</p>
                <p className="text-white/60 text-sm">Years Experience</p>
              </div>
              <div className="text-center">
                <p className="text-electric-teal text-3xl font-bold">Free</p>
                <p className="text-white/60 text-sm">Estimates</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services in This Area */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Electrical Services in {location.name}
            </h2>
            <p className="text-white/60 mb-12 max-w-2xl">
              We provide comprehensive electrical services to {location.name} and surrounding areas. All work is performed by licensed, BC Hydro Alliance certified electricians.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}`}
                  className="bg-secondary-dark p-6 rounded-lg border border-white/10 hover:border-electric-teal/50 transition-all group"
                >
                  <span className="text-3xl mb-4 block">{service.icon}</span>
                  <h3 className="text-white font-bold text-lg mb-2 group-hover:text-electric-teal transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-white/60 text-sm">
                    Professional {service.name.toLowerCase()} services in {location.name}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Service Landing Page Links */}
        <section className="py-16 md:py-20 bg-primary-dark border-t border-white/5" data-testid="service-links-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              {location.name} Electrical Services
            </h2>
            <p className="text-white/40 text-sm mb-8">Dedicated pages for each service we offer in {location.name}. Click for details, pricing, FAQs, and a free quote form.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {title: 'EV Charger Installation', slug: `ev-charger-installation-${slug}`},
                {title: 'Panel Upgrades', slug: `panel-upgrades-${slug}`},
                {title: 'Generator Installation', slug: `generator-installation-${slug}`},
                {title: 'Lighting Installation', slug: `lighting-installation-${slug}`},
                {title: 'EPR Inspection', slug: `epr-inspection-${slug}`},
                {title: 'BC Hydro Power Smart', slug: `bc-hydro-power-smart-${slug}`},
                {title: 'Residential Electrician', slug: `residential-electrician-${slug}`},
                {title: 'Commercial Electrician', slug: `commercial-electrician-${slug}`},
              ].map((svc) => (
                <Link key={svc.slug} to={`/${svc.slug}`}
                  className="bg-secondary-dark rounded-lg p-4 border border-white/5 hover:border-electric-teal/30 transition-colors group"
                  data-testid={`service-link-${svc.slug}`}
                >
                  <p className="text-white font-medium text-sm group-hover:text-electric-teal transition-colors">{svc.title}</p>
                  <p className="text-white/30 text-xs mt-1">in {location.name} &rarr;</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 md:py-24 bg-secondary-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
              Why {location.name} Chooses Island Coastal Electric
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                {location.highlights.map((highlight) => (
                  <div key={highlight} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-electric-teal flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-medium">{highlight}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-start gap-4">
                  <Shield className="w-6 h-6 text-electric-teal flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Fully Licensed & Insured</p>
                    <p className="text-white/60 text-sm">Licensed & insured, full liability coverage</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-electric-teal flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Reliable Scheduling</p>
                    <p className="text-white/60 text-sm">We show up on time, every time</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Award className="w-6 h-6 text-electric-teal flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-white font-medium">Quality Guaranteed</p>
                    <p className="text-white/60 text-sm">All work backed by our warranty</p>
                  </div>
                </div>
              </div>
              <div className="bg-primary-dark p-8 rounded-lg border border-electric-teal/30">
                <h3 className="text-electric-teal font-bold text-xl mb-4">Get a Free Estimate in {location.name}</h3>
                <p className="text-white/70 mb-6">
                  Ready to start your electrical project? Contact us for a free, no-obligation estimate. We&apos;ll come to your {location.name} property and provide upfront pricing.
                </p>
                <div className="space-y-4">
                  <Button asChild className="w-full bg-electric-teal text-primary-dark font-bold">
                    <Link to="/book-estimate">Book Free Estimate</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full border-white/30 text-white">
                    <a href="tel:778-960-2377">
                      <Phone className="mr-2 w-4 h-4" /> Call 778-960-2377
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Areas */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8">Other Areas We Serve on Vancouver Island</h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(locations)
                .filter(([key]) => key !== slug)
                .map(([key, loc]) => (
                  <Link
                    key={key}
                    to={`/areas/${key}`}
                    className="px-4 py-2 bg-secondary-dark rounded-full text-white/70 text-sm hover:text-electric-teal hover:bg-secondary-dark/80 transition-all"
                  >
                    {loc.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LocationPage;
export { locations };
